import { useEffect } from 'react';

import { Box, Skeleton, Stack } from '@mui/material';
import { IoClose } from 'react-icons/io5';

import { ESdkEvents } from '../../contracts';
import { sendFEEvent } from '../../eventUtil';
import { useCheckout } from '../../hooks/use-checkout';
import { useDeviceHelper } from '../../hooks/use-device-helper';
import { useAppConfig } from '../../hooks/use-app-config';
import { useAPI } from '../../services/api.service';
import { ECheckoutStatus, RedirectStatus } from '../../constants/enums';
import { mobileSDKUtils } from '../../utils/mobile-sdk-utils';
import { assignMerchantRedirect, getRedirectUrlWithStatus } from '../../utils/url-utils';

import { usePaymentState } from '../../components/Checkout/hooks/use-payment-state';
import { PendingMessage } from '../../components/Checkout/components/PendingMessage';
import { ErrorMessage } from '../../components/Checkout/components/ErrorMessage';
import { SuccessMessage } from '../../components/Checkout/components/SuccessMessage';
import { Description } from '../../components/Checkout/components/Description/Description';
import { ChargebeePaymentElement } from '../../components/Subscriptions/ChargebeePaymentElement/ChargebeePaymentElement';
import { PaymentElementSkeleton } from '../../Skeltons/PaymentElementSkeleton';
import { FormFooter } from '../../components/Checkout/components/PaymentForm/components/FormFooter/FormFooter';

interface CheckoutProps {
  onClose: () => void;
}

export function Subscriptions({ onClose }: CheckoutProps) {
  const { isMobile, getIosVersion, isOldIos } = useDeviceHelper();
  const { theme, featureFlags } = useAppConfig();
  const { getEventParams, redirectUrl, disableRedirect } = useCheckout();
  const { API } = useAPI();
  const checkoutStatus = usePaymentState((state) => state.checkoutStatus);
  const isPaymentLoading = usePaymentState((state) => state.isPaymentLoading);
  const isPaymentElementLoading = usePaymentState((state) => state.isPaymentElementLoading);
  const showMobileOpenCloseAnimation = Boolean(
    isMobile && (featureFlags?.checkout_open_close_animation || mobileSDKUtils.isMobileSdk())
  );
  const hideSuccessScreen = Boolean(
    featureFlags?.checkout_disable_success_screen &&
      checkoutStatus === ECheckoutStatus.SUCCESS_SHOW_PENDING
  );

  useEffect(() => {
    if (hideSuccessScreen) {
      if (showMobileOpenCloseAnimation) {
        onClose();
      } else {
        sendFEEvent(ESdkEvents.CLOSE_CHECKOUT, {
          ...getEventParams(),
        });
      }
    }
  }, [hideSuccessScreen, onClose, showMobileOpenCloseAnimation]);

  const handleClose = async () => {
    if (checkoutStatus === ECheckoutStatus.INIT) {
      try {
        await API.closeSubscription();
      } catch (error) {
        console.error(error);
      }
    }
    if (showMobileOpenCloseAnimation) {
      onClose();
    } else {
      sendFEEvent(ESdkEvents.CLOSE_CHECKOUT, {
        ...getEventParams(),
      });
    }
  };

  const getMessage = () => {
    if (
      checkoutStatus === ECheckoutStatus.PENDING ||
      checkoutStatus === ECheckoutStatus.SUCCESS_SHOW_PENDING
    ) {
      return <PendingMessage />;
    }

    if (checkoutStatus === ECheckoutStatus.ERROR) {
      return (
        <ErrorMessage
          handleClose={() => {
            const destination =
              redirectUrl && !disableRedirect
                ? getRedirectUrlWithStatus(redirectUrl, RedirectStatus.FAIL)
                : null;
            if (!assignMerchantRedirect(destination)) handleClose();
          }}
        />
      );
    }

    if (checkoutStatus === ECheckoutStatus.SUCCESS) {
      return (
        <SuccessMessage
          handleClose={() => {
            const destination =
              redirectUrl && !disableRedirect
                ? getRedirectUrlWithStatus(redirectUrl, RedirectStatus.SUCCESS)
                : null;
            if (!assignMerchantRedirect(destination)) handleClose();
          }}
        />
      );
    }
    return null;
  };

  const showCloseButton = !(featureFlags?.checkout_deeplinks_disable_store || isPaymentLoading);

  return (
    <>
      {getMessage()}
      <Box
        className={`checkout-container ${
          isOldIos(getIosVersion()) ? 'position-fixed-not-supported-patch' : ''
        } ${mobileSDKUtils.isMobileSdk() ? 'mobile-sdk' : ''}`}
        display={checkoutStatus === ECheckoutStatus.INIT ? 'flex' : 'none'}
      >
        {showCloseButton && (
          <div
            className="close-button"
            onClick={() => {
              const destination =
                redirectUrl && !disableRedirect
                  ? getRedirectUrlWithStatus(redirectUrl, RedirectStatus.CANCEL)
                  : null;
              if (!assignMerchantRedirect(destination)) handleClose();
            }}
            style={{ color: isMobile ? theme?.text : 'black' }}
          >
            <IoClose />
          </div>
        )}
        <Description isSubscription />
        <Box className="payment">
          <Box className="payment-form">
            {isPaymentElementLoading && <Skeletons />}
            <Box display={isPaymentElementLoading ? 'none' : 'block'}>
              <ChargebeePaymentElement />
            </Box>
            <FormFooter isHiddenApmsShown={false} showSubscriptionAgreement />
          </Box>
        </Box>
      </Box>
    </>
  );
}

function Skeletons() {
  return (
    <Stack spacing={2}>
      <PaymentElementSkeleton />
      <PaymentElementSkeleton />
      <PaymentElementSkeleton />
      <Skeleton variant="rounded" width={'100%'} height={45} />
    </Stack>
  );
}
